import { Form } from '@edgebox/react-components';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faArrowDown, faArrowUp, faSort } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link } from 'react-router';
import { HealthCheckNameReference } from '../Onboarding/ContentInventory/Components/HealthCheckNameReference';
import { SkeletonBlock } from './BrandPresenceSkeleton';
import { compareScoresAscending, healthBandOf, isPassingScore, toScorePercentage } from './contentHealth.utils';
import { HEALTH_TINT_CLASSES } from './HealthScoreCell';

/** One tag's row: its average health and, per check, the average score of its pages. */
export type ContentHealthInsightsRow = {
  /** The tag's key, or `null` for the pages without a tag of this kind. */
  term: string | null;
  name: React.ReactNode;
  /** The tag's name as text, for sorting and the links' accessible names. */
  nameText: string;
  averageScore?: number;
  checks: Record<string, number>;
  /** Where the row's pages are listed, when a list can show exactly them. */
  to?: string;
};

type SortState = { by: 'health'; descending: boolean } | { by: 'check'; key: string; descending: boolean };

/**
 * A header cell, without its horizontal padding, which differs between the tag column and the rest. The
 * table takes the height of its rows and the page scrolls it, so the header row scrolls with it; only
 * the tag column is held in place while the table scrolls sideways.
 */
const HEAD_CELL_CLASSES = 'border-b border-slate-200/70 bg-white py-2.5 text-left align-bottom text-xs font-semibold text-slate-600';

/** The sort control in a header cell: the column's name over up to two lines, and its direction. */
function SortHeaderButton({
  children,
  active,
  descending,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  descending: boolean;
  onClick: () => void;
}) {
  const icon = !active ? faSort : descending ? faArrowDown : faArrowUp;
  return (
    <button
      type="button"
      className={`group/sort -mx-1 inline-flex max-w-full items-end gap-1.5 rounded-md bg-transparent px-1 py-0.5 text-left transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
        active ? 'text-slate-900' : ''
      }`}
      onClick={onClick}
    >
      <span className="line-clamp-2 whitespace-normal">{children}</span>
      <FontAwesomeIcon
        icon={icon}
        aria-hidden="true"
        className={`mb-0.5 shrink-0 text-[0.625rem] ${active ? 'text-primary' : 'text-slate-400 opacity-60 group-hover/sort:opacity-100'}`}
      />
    </button>
  );
}

function ScoreCell({ score }: { score?: number }) {
  if (score === undefined) {
    return (
      <span className="flex h-9 items-center justify-center text-sm text-slate-400" title="Not measured">
        <span aria-hidden="true">—</span>
        <span className="sr-only">Not measured</span>
      </span>
    );
  }

  return (
    <span
      className={`flex h-9 items-center justify-center rounded-md text-sm font-semibold tabular-nums ${HEALTH_TINT_CLASSES[healthBandOf(score)]}`}
    >
      {`${toScorePercentage(score)}%`}
    </span>
  );
}

/**
 * Insights: per tag, how its pages score on every check. Rows open the tag's pages in Content
 * Inventory; the header row and the tag column stay in place while the table scrolls.
 */
export function ContentHealthInsightsTable({
  api,
  facetName,
  rows,
  checkKeys,
  checkScores,
  emptyMessage,
  loading,
}: {
  api?: ISyncCoreApi;
  facetName: string;
  rows: ContentHealthInsightsRow[];
  checkKeys: string[];
  /** Each check's average score across all pages, which orders the columns worst first. */
  checkScores: Record<string, number>;
  emptyMessage: string;
  loading?: boolean;
}) {
  const [sort, setSort] = React.useState<SortState>({ by: 'health', descending: false });
  const [hidePassing, setHidePassing] = React.useState(false);
  const hidePassingId = React.useId();

  const passingEverywhere = React.useMemo(
    () =>
      new Set(
        checkKeys.filter((key) => {
          const scores = rows.map((row) => row.checks[key]).filter((score): score is number => score !== undefined);
          return scores.length > 0 && scores.every(isPassingScore);
        })
      ),
    [checkKeys, rows]
  );

  const columns = React.useMemo(
    () =>
      checkKeys
        .filter((key) => !hidePassing || !passingEverywhere.has(key))
        .sort((left, right) => compareScoresAscending(checkScores[left], checkScores[right]) || left.localeCompare(right)),
    [checkKeys, checkScores, hidePassing, passingEverywhere]
  );

  const sortedRows = React.useMemo(() => {
    const scoreOf = (row: ContentHealthInsightsRow) => (sort.by === 'health' ? row.averageScore : row.checks[sort.key]);
    return [...rows].sort((left, right) => {
      const leftScore = scoreOf(left);
      const rightScore = scoreOf(right);
      // A row without a figure stays last in either direction.
      if (leftScore === undefined || rightScore === undefined) {
        return compareScoresAscending(leftScore, rightScore) || left.nameText.localeCompare(right.nameText);
      }
      const order = sort.descending ? rightScore - leftScore : leftScore - rightScore;
      return order || left.nameText.localeCompare(right.nameText);
    });
  }, [rows, sort]);

  const sortBy = (next: { by: 'health' } | { by: 'check'; key: string }) =>
    setSort((current) => {
      const same = current.by === next.by && (next.by === 'health' || (current.by === 'check' && current.key === next.key));
      return { ...next, descending: same ? !current.descending : false };
    });

  const ariaSortOf = (active: boolean): 'ascending' | 'descending' | undefined =>
    active ? (sort.descending ? 'descending' : 'ascending') : undefined;

  if (loading && !rows.length) {
    return (
      <div className="space-y-2" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div key={row} className="grid grid-cols-[12rem_6rem_repeat(5,minmax(0,1fr))] gap-2">
            <SkeletonBlock className={`h-9 ${row % 2 ? 'w-2/3' : 'w-full'}`} />
            {[0, 1, 2, 3, 4, 5].map((cell) => (
              <SkeletonBlock key={cell} className="h-9 w-full rounded-md" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return <p className="m-0 py-6 text-center text-sm text-slate-500">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-3">
      {passingEverywhere.size ? (
        <div className="flex justify-end">
          <Form.Check
            type="switch"
            id={hidePassingId}
            className="mb-0 text-sm text-slate-600"
            checked={hidePassing}
            onChange={() => setHidePassing((value) => !value)}
            label={`Hide checks every tag passes (${passingEverywhere.size})`}
          />
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200/70">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th scope="col" className={`${HEAD_CELL_CLASSES} sticky left-0 z-20 min-w-[13rem] border-r px-3`}>
                {facetName}
              </th>
              <th scope="col" className={`${HEAD_CELL_CLASSES} min-w-[7rem] px-2`} aria-sort={ariaSortOf(sort.by === 'health')}>
                <SortHeaderButton active={sort.by === 'health'} descending={sort.descending} onClick={() => sortBy({ by: 'health' })}>
                  Average health
                </SortHeaderButton>
              </th>
              {columns.map((key) => {
                const active = sort.by === 'check' && sort.key === key;
                return (
                  <th
                    key={`check:${key}`}
                    scope="col"
                    className={`${HEAD_CELL_CLASSES} w-[8.5rem] min-w-[8.5rem] px-2`}
                    aria-sort={ariaSortOf(active)}
                  >
                    <SortHeaderButton active={active} descending={sort.descending} onClick={() => sortBy({ by: 'check', key })}>
                      <HealthCheckNameReference api={api} healthCheckKey={key} fallback={key} />
                    </SortHeaderButton>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const target = row.to;
              return (
                <tr key={`insight:${row.term ?? 'none'}`} className="group/row">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 border-b border-r border-slate-100 bg-white px-3 py-1 text-left text-sm font-semibold text-slate-800 group-hover/row:bg-slate-50"
                  >
                    {target ? (
                      <Link
                        to={target}
                        className="block rounded-md py-1.5 text-inherit no-underline hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        {row.name}
                        <span className="sr-only">: show in Content Inventory</span>
                      </Link>
                    ) : (
                      <span className="block py-1.5">{row.name}</span>
                    )}
                  </th>
                  <td className="border-b border-slate-100 p-1 group-hover/row:bg-slate-50">
                    <ScoreCell score={row.averageScore} />
                  </td>
                  {columns.map((key) => (
                    <td key={`cell:${row.term ?? 'none'}:${key}`} className="border-b border-slate-100 p-1 group-hover/row:bg-slate-50">
                      <ScoreCell score={row.checks[key]} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
